// Path: /home/ladmin/ESTATA/apps/0001-estata.com/frontend/src/components/CookieConsent.tsx
// Version: v3.3.0
// Description: X 20px from all edges, buttons 20px from modal edges

import React, { useState, useEffect, useCallback, useRef } from 'react';

const CONSENT_KEY = '_est_cookie_consent';
const CONSENT_VERSION = '1.0';

interface ConsentState {
  necessary: boolean;
  statistics: boolean;
  marketing: boolean;
  version: string;
  timestamp: string;
}

const getStoredConsent = (): ConsentState | null => {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentState;
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
};

const storeConsent = (consent: ConsentState) => {
  localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
};

const Toggle: React.FC<{ checked: boolean; disabled?: boolean; onChange?: () => void }> = ({ checked, disabled, onChange }) => (
  <button
    type="button"
    onClick={disabled ? undefined : onChange}
    className={`w-12 h-7 rounded-full relative transition-colors duration-200 flex-shrink-0 ${
      checked ? 'bg-blue-500' : 'bg-white/20'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-90'}`}
  >
    <div className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-200 ${
      checked ? 'translate-x-[22px]' : 'translate-x-[3px]'
    }`} />
  </button>
);

const glassStyle: React.CSSProperties = {
  background: 'rgba(15, 15, 25, 0.75)',
  backdropFilter: 'blur(40px) saturate(1.8)',
  WebkitBackdropFilter: 'blur(40px) saturate(1.8)',
  border: '1px solid rgba(255, 255, 255, 0.12)',
  boxShadow: '0 24px 48px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.06)',
};

const PrivacyContent: React.FC = () => (
  <div className="space-y-5 text-sm text-white/80 leading-relaxed">
    <p className="font-semibold text-white/90">
      Der Schutz Ihrer Daten gehört zu den wichtigsten Grundsätzen der ESTATA GmbH.
      Wir möchten Sie mit dieser Datenschutzerklärung über Art, Umfang und Zweck der
      von uns erhobenen, genutzten und verarbeiteten personenbezogenen Daten informieren.
    </p>
    <div>
      <h3 className="text-base font-bold text-white mb-1">1. Datenschutz auf einen Blick</h3>
      <div className="h-px bg-white/10 mb-2" />
      <p>Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. Andere Daten werden automatisch beim Besuch der Website durch unsere IT-Systeme erfasst (z.B. Internetbrowser, Betriebssystem, Uhrzeit des Seitenaufrufs).</p>
    </div>
    <div>
      <h3 className="text-base font-bold text-white mb-1">2. Verantwortliche Stelle</h3>
      <div className="h-px bg-white/10 mb-2" />
      <p>ESTATA GmbH · Katharinenstraße 9, 10711 Berlin<br />Direct: +49 30 6298 7730 · Mail: info@estata.com</p>
    </div>
    <div>
      <h3 className="text-base font-bold text-white mb-1">3. Cookies</h3>
      <div className="h-px bg-white/10 mb-2" />
      <p><strong className="text-white">Notwendige Cookies</strong> — für Grundfunktionen wie Navigation und Zugang zu geschützten Bereichen erforderlich.</p>
      <p className="mt-1"><strong className="text-white">Statistik-Cookies</strong> — helfen uns zu verstehen, wie Besucher die Website nutzen.</p>
      <p className="mt-1"><strong className="text-white">Marketing-Cookies</strong> — ermöglichen personalisierte Werbung auf Basis Ihres Nutzerverhaltens.</p>
    </div>
    <div>
      <h3 className="text-base font-bold text-white mb-1">4. Ihre Rechte</h3>
      <div className="h-px bg-white/10 mb-2" />
      <p>Sie haben jederzeit das Recht auf Auskunft, Berichtigung, Löschung und Einschränkung der Verarbeitung. Sie können Ihre Einwilligung jederzeit über „Cookie-Einstellungen" im Footer widerrufen.</p>
    </div>
    <div>
      <h3 className="text-base font-bold text-white mb-1">5. Datenschutzbeauftragter</h3>
      <div className="h-px bg-white/10 mb-2" />
      <p>Ines Gebhardt · ESTATA GmbH, Katharinenstraße 9, 10711 Berlin · info@estata.com</p>
    </div>
    <p className="text-white/40 text-xs pt-3 border-t border-white/10">
      Vollständige Datenschutzerklärung: estata.com/privacy-policy · Stand: 12.10.2022, Version 1.14
    </p>
  </div>
);

const CookieConsent: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [statistics, setStatistics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const stored = getStoredConsent();
    if (!stored) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    if (showPrivacy && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [showPrivacy]);

  const save = useCallback((stats: boolean, mktg: boolean) => {
    storeConsent({
      necessary: true,
      statistics: stats,
      marketing: mktg,
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString(),
    });
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <>
      <div
        className="fixed inset-0"
        style={{ zIndex: 9998, backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}
      >
        <div className="absolute inset-0 bg-black/40" />
      </div>

      <div
        className="fixed bottom-0 left-0 right-0 flex justify-center p-4 md:p-6"
        style={{ zIndex: 9999, maxHeight: '95vh' }}
      >
        <div
          className="w-full max-w-[860px] rounded-3xl overflow-hidden flex flex-col transition-all duration-300"
          style={{ ...glassStyle, maxHeight: showPrivacy ? '90vh' : 'none' }}
        >

          {/* === Privacy view === */}
          {showPrivacy && (
            <>
              {/* Header — X: 43px circle, 20px from top + right + bottom */}
              <div
                className="relative border-b border-white/10 flex-shrink-0 flex items-center"
                style={{ minHeight: 83, paddingLeft: 20, paddingRight: 75 }}
              >
                <h3 className="text-lg md:text-xl font-bold text-white">Datenschutzerklärung</h3>
                <button
                  onClick={() => setShowPrivacy(false)}
                  className="absolute rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                  style={{ width: 43, height: 43, top: 20, right: 20 }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M12 4L4 12M4 4l8 8" stroke="white" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
              <div ref={scrollRef} className="overflow-y-auto flex-1 pt-5 pb-8" style={{ minHeight: 0, paddingLeft: 20, paddingRight: 20 }}>
                <PrivacyContent />
              </div>
              <div className="border-t border-white/10" />
            </>
          )}

          {/* === Default view === */}
          {!showPrivacy && (
            <div style={{ padding: 20, paddingBottom: 0 }}>
              <h2 className="text-xl md:text-2xl font-bold text-white mb-3">
                Wir respektieren Ihre Privatsphäre
              </h2>
              <p className="text-white/70 text-sm md:text-base leading-relaxed mb-1">
                Wir verwenden Cookies, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten.
                Notwendige Cookies sind für die Funktion der Seite erforderlich. Darüber hinaus können Sie
                auswählen, ob Sie Statistik- und Marketing-Cookies zulassen möchten.
              </p>
              <p className="text-white/50 text-sm mb-5">
                Weitere Informationen finden Sie in unserer{' '}
                <button
                  onClick={() => setShowPrivacy(true)}
                  className="text-blue-400 hover:text-blue-300 underline underline-offset-2 transition-colors"
                >
                  Datenschutzerklärung
                </button>.
              </p>

              {showSettings && (
                <div className="mb-5 space-y-4 border-t border-white/10 pt-5">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <span className="font-semibold text-white text-sm">Notwendig</span>
                      <p className="text-xs text-white/50">Grundfunktionen. Immer aktiv.</p>
                    </div>
                    <Toggle checked={true} disabled />
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <span className="font-semibold text-white text-sm">Statistik</span>
                      <p className="text-xs text-white/50">Hilft uns die Website zu verbessern.</p>
                    </div>
                    <Toggle checked={statistics} onChange={() => setStatistics(!statistics)} />
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <span className="font-semibold text-white text-sm">Marketing</span>
                      <p className="text-xs text-white/50">Personalisierte Werbung und Inhalte.</p>
                    </div>
                    <Toggle checked={marketing} onChange={() => setMarketing(!marketing)} />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* === Buttons — 20px from all modal edges === */}
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0" style={{ padding: 20 }}>
            <button
              onClick={() => save(true, true)}
              className="flex-1 bg-blue-500 hover:bg-blue-400 text-white font-semibold py-3.5 px-6 rounded-2xl transition-colors text-sm md:text-base"
            >
              Alle akzeptieren
            </button>
            {showSettings || showPrivacy ? (
              <button
                onClick={() => save(statistics, marketing)}
                className="flex-1 font-semibold py-3.5 px-6 rounded-2xl transition-colors text-sm md:text-base text-white"
                style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.15)' }}
              >
                Auswahl speichern
              </button>
            ) : (
              <button
                onClick={() => setShowSettings(true)}
                className="flex-1 font-semibold py-3.5 px-6 rounded-2xl transition-colors text-sm md:text-base text-white"
                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}
              >
                Einstellungen
              </button>
            )}
            <button
              onClick={() => save(false, false)}
              className="flex-1 font-semibold py-3.5 px-6 rounded-2xl transition-colors text-sm md:text-base text-white/60 hover:text-white"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
            >
              Nur notwendige
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookieConsent;
